import {
    faCalendar,
    faClock,
    faCopy,
    faFile,
    faFileCsv,
    faFileExcel,
    faFileImport,
    faFilePdf,
    faFileWord,
    faGear,
    faFilter as faOutlinedFilter,
    faPaperclip,
    faSpinner,
    faEnvelope as regularFaEnvelope,
} from "@fortawesome/pro-regular-svg-icons";
import {
    faAddressBook,
    faAngleDown,
    faAngleLeft,
    faAngleRight,
    faAngleUp,
    faArrowDownToLine,
    faArrowRight,
    faArrowRightFromBracket,
    faBuildingFlag,
    faCalendarCheck,
    faCheck,
    faCheckCircle,
    faCircle,
    faCircleExclamation,
    faCircleInfo,
    faClipboardCheck,
    faClipboardListCheck,
    faComment,
    faCommentPlus,
    faCommentQuestion,
    faCommentsQuestionCheck,
    faCrown,
    faDownToLine,
    faEnvelope,
    faEye,
    faFamily,
    faFileCirclePlus,
    faFileContract,
    faFileExclamation,
    faFileMagnifyingGlass,
    faFilePen,
    faFileZip,
    faFilter,
    faFilterCircleXmark,
    faFloppyDisk,
    faFloppyDiskCircleArrowRight,
    faFolder,
    faFolderGear,
    faFolderOpen,
    faGavel,
    faGripDotsVertical,
    faHouseFlag,
    faImage,
    faLightbulbOn,
    faList,
    faListCheck,
    faLocationArrow,
    faLocationCheck,
    faLocationPinSlash,
    faLock,
    faLockOpen,
    faMagnifyingGlass,
    faMagnifyingGlassArrowRight,
    faMap,
    faMapLocation,
    faMemoCircleCheck,
    faMerge,
    faMinus,
    faPaperPlaneTop,
    faParagraph,
    faPenToSquare,
    faPerson,
    faPlus,
    faPlusMinus,
    faPoundSign,
    faPrint,
    faRotateRight,
    faScaleUnbalanced,
    faScrewdriverWrench,
    faSliders,
    faStarOfLife,
    faTowerCell,
    faTrash,
    faTriangleExclamation,
    faUpload,
    faUser,
    faUserCog,
    faUserCrown,
    faUsers,
    faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { alpha, createTheme } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";
import type {} from "@mui/x-date-pickers-pro/themeAugmentation";

const MirageColors = {
    miragePrimary: "#0357a6",
    miragePrimaryHover: alpha("#0357a6", 0.8),
    mirageSmallHover: alpha("#04569f", 0.8),
    mirageSecondaryFocus: alpha("#0357a6", 0.4),
    mirageCard: "#f7f7f7",
    mirageGrey: "#636363",
    mirageSuccessGreen: "#146A6A",
    mirageError: "#E74C3C",
    mirageErrorLight: "#f44336",
    mirageWarning: "#dea500",
    mirageNavigate: "#66b2ff",
};

const MirageTheme = createTheme({
    palette: {
        bcMasterApp: "#E7B10A",
        pastelRed: "#FF6961",
        primary: {
            main: MirageColors.miragePrimary,
        },
        secondary: {
            main: "#000",
        },
        success: {
            main: MirageColors.mirageSuccessGreen,
        },
        error: {
            main: MirageColors.mirageError,
            light: MirageColors.mirageErrorLight,
        },
        warning: { main: MirageColors.mirageWarning },
        grey: {
            "50": "#fafafa",
            "100": "#f5f5f5f5",
            "200": "#eeeeee",
            "300": "#e0e0e0",
            "400": "#bdbdbd",
            "500": "#9e9e9e",
            "600": "#757575",
            "700": "#616161",
            "800": "#424242",
            "900": "#212121",
            A100: "#f5f5f5",
            A200: "#eeeeee",
            A400: "#bdbdbd",
            A700: "#616161",
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
        },
    },
    transitions: {
        duration: {
            shortest: 150,
            shorter: 200,
            short: 250,
            // most basic recommended timing
            standard: 300,
            // this is to be used in complex animations
            complex: 375,
            // recommended when something is entering screen
            enteringScreen: 225,
            // recommended when something is leaving screen
            leavingScreen: 195,
        },
        easing: {
            // This is the most common easing curve.
            easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
            // Objects enter the screen at full velocity from off-screen and
            // slowly decelerate to a resting point.
            easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
            // Objects leave the screen at full velocity. They do not decelerate when off-screen.
            easeIn: "cubic-bezier(0.4, 0, 1, 1)",
            // The sharp curve is used by objects that may return to the screen at any time.
            sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
        },
    },
    components: {
        MuiChip: {
            styleOverrides: {
                root: {
                    "&.Mui-disabled": {
                        opacity: 0.7,
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    padding: "0px",
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    "& .MuiToggleButtonGroup-grouped": {
                        "&:not(:first-of-type)": {
                            borderRadius: "4px",
                        },
                        "&:first-of-type": {
                            borderRadius: "4px",
                        },
                    },
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                },
            },
        },
        MuiButton: {
            variants: [
                {
                    props: {
                        variant: "appBar",
                    },
                    style: {
                        ":disabled": {
                            background: "#00000033",
                            color: "#fff",
                        },
                        textTransform: "none",
                        padding: "0.2rem",
                        margin: "0 1rem",
                    },
                },
            ],
            styleOverrides: {
                root: {
                    textTransform: "none",
                },
            },
        },
        MuiButtonGroup: {
            styleOverrides: {
                grouped: {
                    "&.Mui-disabled": {
                        "&:not(:last-of-type)": {
                            "&:hover": {
                                borderRightColor: "transparent",
                            },
                        },
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                    fontSize: "1rem",
                    "&.Mui-selected": {
                        color: MirageColors.miragePrimary,
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                flexContainer: {
                    gap: "1rem",
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                root: {
                    zIndex: 1202,
                },
            },
        },
        MuiDataGrid: {
            styleOverrides: {
                root: {
                    borderRadius: "15px",
                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": { padding: "6px 10px" },
                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": { padding: "6px 10px" },
                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": { padding: "6px 10px" },
                    "& .MuiDataGrid-row:last-child .MuiDataGrid-cell": {
                        borderBottom: "none", // Remove border for cells in the last row
                    },
                },
                columnHeader: {
                    "& columnHeader--alignLeft": {},
                },
                columnSeparator: {
                    visibility: "visible",
                },
                iconSeparator: {
                    color: "#a9a6a6",
                },
                cell: {
                    borderTop: "hidden",
                    borderRight: "hidden",
                    borderLeft: "1px solid #e0e0e0",
                    borderBottom: "1px solid #e0e0e0",
                    "& fieldset": {
                        border: "none",
                        outline: "none",
                    },
                    "&:first-of-type": {
                        borderLeft: "none",
                    },
                },
                row: {
                    flex: 1,
                    "&:last-of-type": {
                        borderBottomLeftRadius: "15px",
                        borderBottomRightRadius: "15px",
                    },
                },
                footerContainer: {
                    height: 43,
                    minHeight: "auto",
                },
                columnHeaderTitle: {
                    fontWeight: "bold",
                },
                cellContent: {
                    display: "-webkit-box",
                    "-webkit-line-clamp": "5",
                    "-webkit-box-orient": "vertical",
                },
            },
            defaultProps: {
                paginationModel: { pageSize: 10, page: 0 },
                rowHeight: 40,
                columnHeaderHeight: 50,
                filterDebounceMs: 500,
            },
        },
        MuiDatePicker: {
            defaultProps: {
                format: "DD/MM/YYYY",
            },
        },
        MuiDateRangePicker: {
            defaultProps: {
                format: "DD/MM/YYYY",
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                input: {
                    "&.Mui-disabled": {
                        WebkitTextFillColor: "black",
                    },
                },
                root: {
                    "&.Mui-disabled": {
                        backgroundColor: "#EEE",
                    },
                },
            },
        },
        MuiStepConnector: {
            defaultProps: {},
            styleOverrides: {
                root: {
                    top: 9,
                    "&.Mui-active": {
                        borderColor: MirageColors.miragePrimary,
                    },
                    "&.Mui-completed": {
                        color: MirageColors.miragePrimary,
                    },
                    "&.Mui-disabled": {
                        color: "#bdbdbd",
                    },
                },
                line: {
                    borderColor: "inherit",
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                label: {
                    lineHeight: "inherit",
                    "&.MuiStepLabel-alternativeLabel": {
                        marginTop: 3,
                    },
                },
                iconContainer: {
                    "&.Mui-active": {
                        color: MirageColors.miragePrimary,
                    },
                    "&.Mui-completed": {
                        color: MirageColors.miragePrimary,
                    },
                    "&.Mui-disabled": {
                        color: "#bdbdbd",
                    },
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                paper: {
                    wordBreak: "break-word",
                },
            },
        },
    },
    icons: {
        sizeS: { width: "16px", height: "16px" },
        sizeM: { width: "24px", height: "24px" },
        sizeL: { width: "42px", height: "42px" },
        editIcon: faPenToSquare,
        closeIcon: faXmark,
        saveIcon: faFloppyDisk,
        deleteIcon: faTrash,
        plusIcon: faPlus,
        spinnerIcon: faSpinner,
        minusIcon: faMinus,
        uploadIcon: faUpload,
        filterIcon: faFilter,
        deleteFilterIcon: faFilterCircleXmark,
        recordInsertedIcon: faFileCirclePlus,
        recordsProcessedIcon: faFolderGear,
        upToDateIcon: faCalendarCheck,
        locationCheckedIcon: faLocationCheck,
        locationSlashedIcon: faLocationPinSlash,
        warningIcon: faTriangleExclamation,
        successIcon: faCheckCircle,
        starOfLifeIcon: faStarOfLife,
        sendIcon: faPaperPlaneTop,
        envelopeIcon: faEnvelope,
        emailIcon: regularFaEnvelope,
        infoIcon: faCircleInfo,
        angleRightIcon: faAngleRight,
        angleLeftIcon: faAngleLeft,
        angleUpIcon: faAngleUp,
        angleDownIcon: faAngleDown,
        arrowRightIcon: faArrowRight,
        userIcon: faUser,
        userCrownIcon: faUserCrown,
        logoutIcon: faArrowRightFromBracket,
        manageUsersIcon: faUsers,
        accountSettingsIcon: faUserCog,
        folder: faFolder,
        folderOpen: faFolderOpen,
        fileMagnifyingGlass: faFileMagnifyingGlass,
        poundIcon: faPoundSign,
        list: faList,
        map: faMap,
        clipboardListCheck: faClipboardListCheck,
        scaleUnbalanced: faScaleUnbalanced,
        addressBook: faAddressBook,
        family: faFamily,
        toolsIcon: faScrewdriverWrench,
        commentQuestionIcon: faCommentQuestion,
        commentPlusIcon: faCommentPlus,
        bcMasterAppIcon: faCrown,
        circleIcon: faCircle,
        refreshIcon: faRotateRight,
        bcPlansCheckIcon: faClipboardCheck,
        bcDomesticIcon: faHouseFlag,
        bcNonDomesticIcon: faBuildingFlag,
        calendarIcon: faCalendar,
        clockIcon: faClock,
        fileIcon: faFile,
        calculateIcon: faPlusMinus,
        personIcon: faPerson,
        saveAndNewIcon: faFloppyDiskCircleArrowRight,
        checkIcon: faCheck,
        attachmentIcon: faPaperclip,
        towerIcon: faTowerCell,
        printIcon: faPrint,
        lightBulbOnIcon: faLightbulbOn,
        downloadIcon: faDownToLine,
        zipIcon: faFileZip,
        copyIcon: faCopy,
        fileExclamation: faFileExclamation,
        lockOpenIcon: faLockOpen,
        lockClosedIcon: faLock,
        faListCheck: faListCheck,
        commentIcon: faComment,
        outlinedFilterIcon: faOutlinedFilter,
        locationArrowIcon: faLocationArrow,
        mergeIcon: faMerge,
        pdfIcon: faFilePdf,
        gripDotsVerticalIcon: faGripDotsVertical,
        slidersIcon: faSliders,
        commentsQuestionCheckIcon: faCommentsQuestionCheck,
        eyeIcon: faEye,
        mapLocationIcon: faMapLocation,
        filePenIcon: faFilePen,
        gavelIcon: faGavel,
        importIcon: faFileImport,
        goToIcon: faMagnifyingGlassArrowRight,
        gearIcon: faGear,
        arrowDownToLineIcon: faArrowDownToLine,
        excelIcon: faFileExcel,
        wordIcon: faFileWord,
        csvIcon: faFileCsv,
        image: faImage,
        dangerIcon: faCircleExclamation,
        magnifyingGlassIcon: faMagnifyingGlass,
        fileContractIcon: faFileContract,
        memoCircleCheckIcon: faMemoCircleCheck,
        paragraphIcon: faParagraph,
    },
    symbols: {
        squareMeters: "\u33A1",
        percent: "\u0025",
        pound: "\u00A3",
    },
    map: {
        markerIcons: {
            green: {
                iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png",
                shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41],
            },
            blue: {
                iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png",
                shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png",
                iconSize: [25, 41],
                iconAnchor: [12, 41],
                popupAnchor: [1, -34],
                shadowSize: [41, 41],
            },
        },
        attributions: {
            OSAttribution: `<a href="https://www.ordnancesurvey.co.uk/governance/crown-copyright" target="_blank">Contains Ordnance Survey data © Crown copyright 
            and database right ${new Date().getFullYear()}</a>`,
        },
    },
});

export { MirageTheme };
